import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import { getCaseBySlug, getRelatedCases } from "@/mocks/cases";
import { CaseQuotes, CaseTimeline } from "./components/CaseBlocks";

const formatDate = (value: string) => value.replace(/-/g, ".");

const sections = [
  { id: "overview", label: "事件概览" },
  { id: "focus", label: "争议焦点" },
  { id: "timeline", label: "旧料时间线" },
  { id: "quotes", label: "过往言论" },
  { id: "outcome", label: "后续结果" },
  { id: "related", label: "相关推荐" },
];

const Page = () => {
  const { slug } = useParams<{ slug: string }>();
  const item = slug ? getCaseBySlug(slug) : undefined;

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-400" />
            <h1 className="mt-5 font-heading font-black text-2xl md:text-3xl text-foreground-950">
              没有找到这份档案
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              它可能已被修订、合并，或者链接不太对。
            </p>
            <Link
              to="/cases"
              className="mt-7 inline-flex px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回塌房合集
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedCases(item, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `${item.name} 塌房档案：旧料时间线与过往言论整理`,
      description: item.summary,
      inLanguage: "zh-CN",
      articleSection: item.category,
      datePublished: item.fallDate,
      dateModified: item.fallDate,
      author: { "@type": "Organization", name: "塌房档案局" },
      publisher: { "@type": "Organization", name: "塌房档案局" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://hnhxedu.cn/" },
        { "@type": "ListItem", position: 2, name: "塌房合集", item: "https://hnhxedu.cn/cases" },
        {
          "@type": "ListItem",
          position: 3,
          name: item.name,
          item: `https://hnhxedu.cn/cases/${item.slug}`,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${item.name}塌房档案 - 旧料时间线与过往言论整理`}
        description={`${item.name}（${item.category}）塌房事件档案：${item.summary.slice(0, 60)}… 含旧料时间线、过往言论与争议焦点整理。`}
        keywords={`${item.name},网红塌房,网红塌房黑历史,${item.category},${item.tags.join(",")}`}
        path={`/cases/${item.slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="relative w-full bg-foreground-950">
          <div className="absolute inset-0 w-full h-full">
            <img
              src={item.cover}
              alt={`${item.name} 塌房事件档案背景图`}
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/60" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
            <div className="[&_a]:text-background-300 [&_a:hover]:text-primary-200 [&_span]:text-background-200">
              <Breadcrumbs
                items={[
                  { label: "首页", href: "/" },
                  { label: "塌房合集", href: "/cases" },
                  { label: item.name },
                ]}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-5">
              <img
                src={item.avatar}
                alt={`${item.name} 头像`}
                className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover border-2 border-background-50/20"
              />
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                  <i className="ri-price-tag-3-line" />
                  {item.category}
                </span>
                <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl text-background-50">
                  {item.name} 塌房档案
                </h1>
                <p className="mt-2 text-sm text-background-300">
                  {item.handle} · {item.platform} · {formatDate(item.fallDate)} 归档
                </p>
              </div>
              <div className="sm:ml-auto text-center px-5 py-3 rounded-lg bg-background-50/10 border border-background-50/20">
                <p className="font-heading font-black text-3xl md:text-4xl text-background-50">
                  {item.heat}
                </p>
                <p className="text-xs text-background-300 mt-1">讨论热度</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <article className="lg:col-span-8">
              <div id="overview" className="scroll-mt-28">
                <h2 className="font-heading font-black text-xl md:text-3xl text-foreground-950 flex items-center gap-2">
                  <i className="ri-file-text-line text-primary-500" />
                  事件概览
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                  {item.summary}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-md text-xs md:text-sm bg-secondary-100 text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div id="focus" className="mt-12 scroll-mt-28">
                <h2 className="font-heading font-black text-xl md:text-3xl text-foreground-950 flex items-center gap-2">
                  <i className="ri-focus-3-line text-primary-500" />
                  争议焦点
                </h2>
                <ul className="mt-5 space-y-3">
                  {item.focus.map((point, index) => (
                    <li
                      key={point}
                      className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/80"
                    >
                      <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-accent-500 text-background-50 text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm md:text-base text-foreground-800 leading-relaxed">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div id="timeline" className="mt-12 scroll-mt-28">
                <h2 className="font-heading font-black text-xl md:text-3xl text-foreground-950 flex items-center gap-2">
                  <i className="ri-history-line text-primary-500" />
                  旧料时间线
                </h2>
                <p className="mt-3 text-sm text-foreground-500">
                  按发生顺序还原，从最初的一句表达到旧账集中翻出的全过程。
                </p>
                <CaseTimeline events={item.timeline} />
              </div>

              <div id="quotes" className="mt-12 scroll-mt-28">
                <h2 className="font-heading font-black text-xl md:text-3xl text-foreground-950 flex items-center gap-2">
                  <i className="ri-chat-quote-line text-primary-500" />
                  过往言论
                </h2>
                <p className="mt-3 text-sm text-foreground-500">
                  以下是整理出来的公开表达片段，仅标注日期与来源类型，供你自行对照。
                </p>
                <CaseQuotes quotes={item.quotes} />
              </div>

              <div id="outcome" className="mt-12 scroll-mt-28">
                <h2 className="font-heading font-black text-xl md:text-3xl text-foreground-950 flex items-center gap-2">
                  <i className="ri-flag-2-line text-primary-500" />
                  后续结果
                </h2>
                <p className="mt-4 p-5 rounded-lg bg-background-100 border border-background-200/80 text-sm md:text-base text-foreground-700 leading-loose">
                  {item.outcome}
                </p>
              </div>

              <div className="mt-12 p-5 md:p-6 rounded-lg bg-background-100 border border-dashed border-background-300">
                <h3 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-error-warning-line text-primary-500" />
                  内容说明
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  本篇为网络公开讨论的娱乐整理与评论，人物使用化名，不对应任何真实个人。时间线节点与言论均标注日期和来源类型，
                  如内容与事实存在出入，欢迎通过页面底部邮箱提供依据，我们会核对后修订或下架。
                </p>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <nav
                  aria-label="本页目录"
                  className="p-5 rounded-lg bg-background-100 border border-background-200/80"
                >
                  <h3 className="font-heading font-bold text-base text-foreground-950 mb-4">
                    本页目录
                  </h3>
                  <ul className="space-y-2.5 text-sm">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className="flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors"
                        >
                          <i className="ri-arrow-right-s-line text-primary-400" />
                          {section.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="p-5 rounded-lg bg-accent-500 text-background-50">
                  <h3 className="font-heading font-bold text-base">有新的线索？</h3>
                  <p className="mt-2 text-sm text-accent-50 leading-relaxed">
                    如果你掌握该事件的补充旧料或不同版本，欢迎投给编辑团队一起核对。
                  </p>
                  <Link
                    to="/about#submit"
                    className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium bg-background-50 text-accent-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    投稿线索
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>

                <div className="p-5 rounded-lg bg-background-100 border border-background-200/80">
                  <h3 className="font-heading font-bold text-base text-foreground-950 mb-4">
                    档案信息
                  </h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500">归档分类</dt>
                      <dd className="text-foreground-900 font-medium text-right">{item.category}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500">活跃平台</dt>
                      <dd className="text-foreground-900 font-medium text-right">{item.platform}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500">塌房时间</dt>
                      <dd className="text-foreground-900 font-medium text-right">
                        {formatDate(item.fallDate)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-foreground-500">时间线节点</dt>
                      <dd className="text-foreground-900 font-medium text-right">
                        {item.timeline.length} 个
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section id="related" className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 scroll-mt-28">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <h2 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">
                相关推荐
              </h2>
              <Link
                to="/cases"
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                返回合集
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((rel) => (
                <CaseCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
};

export default Page;